/**
 * SeedArticlesButton — admin one-click action to push the 15 SEO seed
 * articles into the current environment's Mongo DB. Calls a super-admin-only
 * endpoint (POST /api/articles/seed/import) that's safe to re-run (idempotent).
 *
 * Use case: after deploying to production, super admin clicks this button once
 * to populate the production /articles blog without needing shell access.
 */
import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Newspaper, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SeedArticlesButton({ token }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const runImport = async () => {
    if (busy) return;
    if (!window.confirm('Import the 15 seeded SEO articles into this environment?\n\nSafe to re-run — existing articles are skipped.')) {
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/articles/seed/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || `Import failed (${res.status})`);
        setBusy(false);
        return;
      }
      setResult(data);
      const ins = data.inserted?.length || 0;
      const skp = data.skipped?.length || 0;
      const fld = data.failed?.length || 0;
      if (ins > 0) {
        toast.success(`Imported ${ins} articles. Visit /articles to see them live.`);
      } else if (skp > 0 && fld === 0) {
        toast.message(`All ${skp} articles already exist here — nothing to do.`);
      } else {
        toast.error('Import finished with errors. Check the result below.');
      }
    } catch (e) {
      toast.error('Network error during import');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-zinc-900/60 border-zinc-800 mb-6" data-testid="seed-articles-card">
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-lg bg-rooam-blue/10 border border-rooam-blue/30 flex items-center justify-center shrink-0">
            <Newspaper className="w-5 h-5 text-rooam-blue" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">Populate /articles blog</h3>
            <p className="text-zinc-400 text-xs mt-0.5">
              Pushes the 15 pre-written SEO articles into this environment's database.
              Safe to re-run — existing articles are skipped.
            </p>
          </div>
        </div>
        <Button
          onClick={runImport}
          disabled={busy}
          className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold shrink-0"
          data-testid="seed-articles-btn"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              Importing…
            </>
          ) : (
            <>
              <Newspaper className="w-4 h-4 mr-1.5" />
              Import Articles
            </>
          )}
        </Button>
      </CardContent>
      {result && (
        <CardContent className="border-t border-zinc-800 p-4 pt-3" data-testid="seed-articles-result">
          <div className="flex items-center gap-2 text-sm mb-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span className="text-zinc-300">
              Inserted <span className="font-bold text-emerald-400">{result.inserted?.length || 0}</span> &middot;
              Skipped <span className="font-bold text-zinc-400">{result.skipped?.length || 0}</span> &middot;
              Failed <span className={`font-bold ${result.failed?.length ? 'text-red-400' : 'text-zinc-500'}`}>{result.failed?.length || 0}</span>
            </span>
          </div>
          {result.failed && result.failed.length > 0 && (
            <details className="text-xs text-zinc-500">
              <summary className="cursor-pointer hover:text-zinc-300">View failed entries</summary>
              <pre className="mt-2 p-2 bg-black rounded overflow-auto max-h-40">
                {JSON.stringify(result.failed, null, 2)}
              </pre>
            </details>
          )}
        </CardContent>
      )}
    </Card>
  );
}
